import { useNode, useEditor, ROOT_NODE } from '@craftjs/core';
import React, { useCallback, useEffect, useRef } from 'react';
import { isMouseInsideElement, useThemeInDndShadow } from './helpers';

type Props = {
	render: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
};

export const RenderNode = ({ render }: Props) => {
	const { isHovered, dom, id } = useNode((node) => ({
		isHovered: node.events.hovered,
		dom: node.dom
	}));

	const { isSelected, isCurrentDropTarget, isDrag, isRoot } = useEditor(
		(state, query) => ({
			isSelected: query.getEvent('selected').contains(id),
			isCurrentDropTarget: state.indicator?.placement.parent.id === id,
			isDrag: !!state.indicator?.placement,
			isRoot: id === ROOT_NODE
		})
	);

	const craftJsRendererRef = useRef(
		document.querySelector('.craftjs-renderer')
	);

	const getTheme = useCallback(
		() => craftJsRendererRef.current?.getAttribute('data-theme') || '',
		[craftJsRendererRef]
	);

	useThemeInDndShadow(isSelected ? dom : null, getTheme);

	useEffect(() => {
		if (isRoot && dom) {
			if (isDrag) {
				// hover is not working on DnD so we need another indication
				dom.setAttribute('component-drag-on', 'true');

				window.addEventListener(
					'dragend',
					(e) => {
						// when drag ends, we want to decide if DnD hover indication should be removed or not
						if (isMouseInsideElement(dom, e.clientX, e.clientY)) {
							dom.addEventListener(
								'mouseleave',
								() => dom.removeAttribute('component-drag-on'),
								{ once: true }
							);
						} else {
							dom.removeAttribute('component-drag-on');
						}
					},
					{ once: true }
				);
			} else {
				// sometimes after DnD, selected element has no indication, this workaround is solving it
				const event = new MouseEvent('mouseover');
				dom.dispatchEvent(event);
			}
		}
	}, [dom, isDrag, isRoot]);

	useEffect(() => {
		if (dom) {
			if (isSelected) {
				dom.setAttribute('component-selected', 'true');
			} else {
				dom.removeAttribute('component-selected');
			}
		}
	}, [dom, isSelected]);

	useEffect(() => {
		if (dom) {
			if (isHovered) {
				dom.setAttribute('component-hovered', 'true');
			} else {
				dom.removeAttribute('component-hovered');
			}
		}
	}, [dom, isHovered]);

	useEffect(() => {
		if (dom) {
			if (isCurrentDropTarget) {
				dom.setAttribute('component-drop-target', 'true');
			} else {
				dom.removeAttribute('component-drop-target');
			}
		}
	}, [dom, isCurrentDropTarget]);

	return render;
};
